import { useState } from 'react';
import logger from '@widgets/logger';
import { Controller, useFormContext } from 'react-hook-form';
import { Typography } from '@components/Typography';
import { Switch } from '@components/Switch';
import { Input } from '@components/Input';
import ImageUploaderSingle from '@components/ImageUploaderSingle';
import OtpCode from '@components/OtpCode';
import { useTranslation } from '@hooks';
import { couponsService } from '@services';
import { KB } from '@constants/file';

const Settings = ({ isView }) => {
    const [isUploading, setIsUploading] = useState(false);

    const { control, getValues, setValue, watch, clearErrors } = useFormContext();
    const { t } = useTranslation();

    const coverPhotoUrl = watch('coverPhotoUrl');
    const pinEnabled = watch('pinEnabled');
    const coverPhotoEnabled = watch('coverPhotoEnabled');
    const defaultMessageEnabled = watch('defaultMessageEnabled');

    const uploadLogo = async e => {
        try {
            setIsUploading(true);
            const data = new FormData();
            data.append('file', e.target.files[0]);
            data.append('fileName', e.target.files[0].name);
            const response = await couponsService.uploadCouponImage(data);
            setValue('coverPhotoUrl', response.publicUrl, { shouldDirty: true, shouldValidate: true });
        } catch (_) {
            logger.error("Couldn't upload cover photo for coupon");
        }
        setIsUploading(false);
        e.target.value = '';
    };

    const onDeleteLogo = () => setValue('coverPhotoUrl', '', { shouldDirty: true, shouldValidate: true });

    return (
        <div>
            <Typography variant="subtitle1" sx={{ color: 'var(--text-primary)', marginBottom: '8px' }}>
                {t('common.settings')}
            </Typography>
            <div>
                <Controller
                    control={control}
                    name="defaultMessageEnabled"
                    render={({ field, ref }) => (
                        <Switch
                            {...field}
                            ref={ref}
                            checked={getValues('defaultMessageEnabled')}
                            handleChange={event => {
                                setValue('defaultMessageEnabled', event.target.checked, {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                });
                                if (!event.target.checked) {
                                    setValue('defaultMessage', '', { shouldDirty: true, shouldValidate: true });
                                }
                            }}
                            label={t('CouponsPage.defaultMessage')}
                            disabled={isView}
                        />
                    )}
                />
                {defaultMessageEnabled ? (
                    <Controller
                        control={control}
                        name="defaultMessage"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Input
                                ref={ref}
                                inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                label={t('common.message', { count: 1 })}
                                fullWidth
                                error={!!error}
                                helperText={error ? t(error.message) : null}
                                multiline
                                required
                                minRows={4}
                                disabled={isView}
                            />
                        )}
                    />
                ) : null}
            </div>
            <div>
                <Controller
                    control={control}
                    name="coverPhotoEnabled"
                    render={({ field, ref }) => (
                        <Switch
                            {...field}
                            ref={ref}
                            checked={getValues('coverPhotoEnabled')}
                            handleChange={event => {
                                setValue('coverPhotoEnabled', event.target.checked, {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                });
                                if (!event.target.checked) {
                                    setValue('coverPhotoUrl', '', { shouldDirty: true, shouldValidate: true });
                                }
                            }}
                            label={t('CampaignsPage.addCoverPhoto')}
                            disabled={isView}
                        />
                    )}
                />
                {coverPhotoEnabled ? (
                    <>
                        <Typography
                            variant="caption"
                            sx={{ color: 'var(--text-secondary)', margin: '4px 0 8px', display: 'block' }}
                        >
                            {t('common.imageUploadFormat', { size: '500KB' })}
                        </Typography>
                        <ImageUploaderSingle
                            maxSize={KB * 500}
                            minSize={10 * KB}
                            extensions="image/*"
                            cropConfig={{ aspectRatio: 1, outputWidth: 250 }}
                            logoUrl={coverPhotoUrl}
                            disabled={isUploading || isView}
                            onDelete={onDeleteLogo}
                            uploadLogo={uploadLogo}
                            style={{ marginBottom: 16 }}
                            isUploading={isUploading}
                        />
                    </>
                ) : null}
            </div>
            <div>
                <Controller
                    control={control}
                    name="pinEnabled"
                    render={({ field, ref }) => (
                        <Switch
                            {...field}
                            ref={ref}
                            checked={getValues('pinEnabled')}
                            handleChange={event => {
                                setValue('pinEnabled', event.target.checked, {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                });
                                if (!event.target.checked) {
                                    setValue('pin', '', { shouldDirty: true, shouldValidate: true });
                                    clearErrors('pin');
                                }
                            }}
                            label={t('CouponsPage.activationPinCode')}
                            disabled={isView}
                        />
                    )}
                />
                {pinEnabled ? (
                    <>
                        <Typography
                            variant="caption"
                            sx={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}
                        >
                            {t('CouponsPage.requireStaffForPin')}
                        </Typography>
                        <Controller
                            control={control}
                            name="pin"
                            render={({ field, fieldState: { error } }) => (
                                <OtpCode
                                    length={4}
                                    {...field}
                                    error={!!error}
                                    helperText={error ? t(error.message, { count: 4 }) : null}
                                    disabled={isView}
                                    style={{ width: 'calc(50% - 4px)' }}
                                />
                            )}
                        />
                    </>
                ) : null}
            </div>
        </div>
    );
};

export default Settings;
